import { Paper } from '@mui/material';
import Box from '@mui/material/Box';
import _ from 'lodash';
import { DateTime } from 'luxon';
import qs from 'qs';
import React, { useContext, useState } from 'react';
import { View, Views } from 'react-big-calendar';
import { SetURLSearchParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ROUTE_PATHS } from '../../../../routes/Routes';
import { useGetAllEvents } from '../../../../services/react-query/events';
import { Event } from '../../../../types/Event';
import { Loading } from '../../../../uikit';
import { formatDateRangeForAPI } from '../../../../utility/dateUtils';
import Calendar from '../Calender/Calender';
import { FilterContext } from '../../../layouts/EventsLayout/EventsLayout';

interface ICalendarEvent {
  uuid: string;
  title: string;
  start: Date;
  end: Date;
}

// this hack is required since the onNavigate and onView fire in immediate succession when a single day is clicked
// we need to accumulate the changes and send them to the debounced URL update function otherwise they destructively interfere with each other
let accu = {};
const updateSearch = _.debounce((setSearch: SetURLSearchParams) => {
  setSearch((prev) => {
    const prevObj = qs.parse(prev.toString());
    return qs.stringify({ ...prevObj, ..._.cloneDeep(accu) });
  });
  accu = {};
}, 20);

const EventCalendar: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const anchorDate = DateTime.fromISO(searchParams.get('date') || DateTime.now().toISO());
  if (!anchorDate.isValid) return null;

  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: anchorDate.minus({ days: 35 }).toISODate(),
    endDate: anchorDate.plus({ days: 35 }).toISODate(),
  });
  const handleRangeChange = (range: Date[] | { start: Date; end: Date }) => {
    const { formattedStart, formattedEnd } = formatDateRangeForAPI(range);
    setDateRange({ startDate: formattedStart, endDate: formattedEnd });
  };

  const [search, setSearch] = useSearchParams();
  const updateDisplay = (updateObj: { date?: string; view?: string }) => {
    accu = { ...accu, ...updateObj };
    updateSearch(setSearch);
  };

  const filters = useContext(FilterContext);
  const { data, isLoading: isGetAllEventsLoading } = useGetAllEvents({
    params: {
      per_page: 0,
      filters: {
        ...filters,
        date_range: [dateRange.startDate, dateRange.endDate],
      },
    },
    enabled: filters !== undefined,
  });

  if (isGetAllEventsLoading) {
    return <Loading />;
  }

  const events = data?.data || [];
  const calendarEvents: ICalendarEvent[] = _.map(events, (event: Event) => {
    return {
      uuid: event.uuid,
      title: event.name,
      start: DateTime.fromISO(event.start_at).toJSDate(),
      end: DateTime.fromISO(event.end_at).toJSDate(),
    };
  });

  const handleEventSelect = (event: any) => {
    navigate(`/${ROUTE_PATHS.EVENTS}/${event.uuid}`);
  };

  const searchObj = qs.parse(search.toString());

  return (
    <Box component={Paper} sx={{ p: 1, borderRadius: 4 }}>
      <Calendar
        events={calendarEvents as ICalendarEvent[]}
        startAccessor="start"
        endAccessor="end"
        showMultiDayTimes
        selectable
        popup
        onSelectEvent={handleEventSelect}
        onRangeChange={handleRangeChange}
        date={searchObj.date ? DateTime.fromISO(searchObj.date as string).toJSDate() : DateTime.now().toJSDate()}
        view={searchObj.view ? (searchObj.view as View) : Views.MONTH}
        onView={(view) => updateDisplay({ view })}
        onNavigate={(date) => updateDisplay({ date: DateTime.fromJSDate(date).toISODate() as string })}
      />
    </Box>
  );
};

export default EventCalendar;
